import React from 'react';
import { motion } from 'framer-motion';
import { Terminal, Mail, MapPin, PlayCircle } from 'lucide-react';

export const Footer: React.FC = () => {
  return (
    <footer className="bg-slate-950 text-white py-16 border-t border-primary/20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          <div className="col-span-1 lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="text-primary">
                <Terminal size={28} />
              </div>
              <h2 className="text-white font-display text-2xl font-bold tracking-tight">
                The Cracked Dev
              </h2>
            </div>
            <p className="text-slate-400 max-w-sm mb-8">
              Building intelligent systems that push the boundaries of human potential. 
              Focused on AI for Social Impact and Technical Excellence.
            </p>
            <div className="flex gap-4">
              <motion.a 
                whileHover={{ scale: 1.1 }}
                className="h-10 w-10 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:text-primary hover:border-primary transition-all" 
                href="https://github.com/31133498"
                target="_blank"
                rel="noopener noreferrer"
                title="GitHub"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
              </motion.a>
              <motion.a 
                whileHover={{ scale: 1.1 }}
                className="h-10 w-10 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:text-primary hover:border-primary transition-all" 
                href="https://www.linkedin.com/in/sanni-shazily-bba942266/?skipRedirect=true"
                target="_blank"
                rel="noopener noreferrer"
                title="LinkedIn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </motion.a>
              <motion.a 
                whileHover={{ scale: 1.1 }}
                className="h-10 w-10 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:text-primary hover:border-primary transition-all" 
                href="mailto:sannishazily@gmail.com"
                title="Email"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
              </motion.a>
              <motion.a 
                whileHover={{ scale: 1.1 }}
                className="h-10 w-10 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:text-primary hover:border-primary transition-all" 
                href="https://www.youtube.com/@ShazdTechbro"
                target="_blank"
                rel="noopener noreferrer"
                title="YouTube"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </motion.a>
            </div>
          </div>
          
          <div className="flex items-center justify-center">
            <div className="w-20 h-20 rounded-full border-2 border-primary/30 overflow-hidden">
              <img 
                className="w-full h-full object-cover" 
                alt="Sanni Shazily Oladipupo" 
                src="/Passports  (1).jpg"
              />
            </div>
          </div>
          
          <div>
            <h5 className="text-white font-display font-bold mb-6 uppercase tracking-widest text-sm">
              Navigation
            </h5>
            <ul className="space-y-4 text-slate-400">
              <li><a className="hover:text-primary transition-colors" href="#about">About Me</a></li>
              <li><a className="hover:text-primary transition-colors" href="#projects">Project Gallery</a></li>
              <li><a className="hover:text-primary transition-colors" href="#skills">Tech Stack</a></li>
              <li><a className="hover:text-primary transition-colors" href="#timeline">Career Timeline</a></li>
              <li><a className="hover:text-primary transition-colors" href="https://github.com/31133498" target="_blank" rel="noopener noreferrer">GitHub</a></li>
            </ul>
          </div>
          
          <div>
            <h5 className="text-white font-display font-bold mb-6 uppercase tracking-widest text-sm">
              Contact
            </h5>
            <ul className="space-y-4 text-slate-400">
              <li className="flex items-center gap-3">
                <Mail size={14} className="text-primary" />
                <a href="mailto:sannishazily@gmail.com" className="hover:text-primary transition-colors">sannishazily@gmail.com</a>
              </li>
              <li className="flex items-center gap-3">
                <MapPin size={14} className="text-primary" />
                Lagos, Nigeria
              </li>
              <li className="flex items-center gap-3">
                <PlayCircle size={14} className="text-primary" />
                <a href="https://www.youtube.com/@ShazdTechbro" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">YouTube</a>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="pt-8 border-t border-slate-900 flex flex-col md:flex-row items-center justify-between gap-6">
          <p className="text-slate-500 text-sm">
            © 2025 Sanni Shazily Oladipupo. All rights reserved.
          </p>
          <p className="text-slate-500 text-sm font-display italic">
            I am Sanni Shazily. If only I knew better, I would do better.
          </p>
        </div>
      </div>
    </footer>
  );
};